import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Star, Clock, Users, Shield, ArrowRight, ExternalLink, Sparkles, CheckCircle, Award, ThumbsUp, Fence } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

const QUOTE_URL = "https://tradehq.com.au/albanyfencingcontractors/enquire";
const PHONE_NUMBER = "0438 185 148";

// Logo URLs
const LOGO = {
  main: "/logo-transparent.png",
  horizontal: "/logo-hero-final.png",
};

// PHOTOS - All unique project photos from chat history (NO stock images, NO duplicates)
const PHOTOS = {
  heroBackground: "https://customer-assets.emergentagent.com/job_bdb8c160-4614-427e-80e0-57a5d33ec243/artifacts/qzme76np_image.png",
  // Service category images - all unique fence photos
  colorbond: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/gkl4qj3k_image.png",
  aluminium: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/yogjifcv_image.png",
  rural: "https://customer-assets.emergentagent.com/job_bdb8c160-4614-427e-80e0-57a5d33ec243/artifacts/g1scnr27_image.png",
  commercial: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/020p539b_image.png",
  // User's exact before/after transformation images (higher quality)
  transformationBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/544sww3u_IMG_8905.jpeg",
  transformationAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/8b0k4zqk_IMG_8905.jpeg",
};

// Portfolio gallery images - 4 rows of unique fence photos (NO duplicates)
const PORTFOLIO_IMAGES = {
  // Row 1 - Static grid (4 unique photos)
  row1: [
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/gkl4qj3k_image.png", title: "Pool Fencing" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/yogjifcv_image.png", title: "Residential Slat" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/dtu1img1_image.png", title: "Colorbond Steel" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/n9msqo8c_image.png", title: "Aluminium Gate" },
  ],
  // Row 2 - Auto-scrolling carousel (8 unique photos - different from row 1)
  row2Carousel: [
    { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/uozweq9v_image.png", title: "Commercial Security" },
    { url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/qgfvwvry_image.png", title: "White Rural Fence" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/wrt20m0j_image.png", title: "Rural Wire Fence" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/2vjc2lid_image.png", title: "Farm Fencing" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/n93k06k2_image.png", title: "Industrial Fence" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/rtpdzikr_image.png", title: "Security Fencing" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/020p539b_image.png", title: "Privacy Screening" },
    { url: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/blnikvv7_image.png", title: "Commercial Fence" },
  ],
};

// Service categories for hero section - with gallery images for popup
const SERVICE_CATEGORIES = [
  { 
    title: "COLORBOND", 
    image: PHOTOS.colorbond, 
    subtitle: "Steel Fencing",
    description: "Premium steel fencing in a wide range of modern colours. Durable, low-maintenance, and perfect for residential and commercial properties.",
    gallery: [
      PHOTOS.colorbond,
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/dtu1img1_image.png",
      PHOTOS.commercial,
    ]
  },
  { 
    title: "ALUMINIUM", 
    image: PHOTOS.aluminium, 
    subtitle: "Slat & Gates",
    description: "Modern aluminium slat fencing and custom gates. Sleek, durable, and perfect for contemporary homes seeking privacy with style.",
    gallery: [
      PHOTOS.aluminium,
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/n9msqo8c_image.png",
    ]
  },
  { 
    title: "RURAL", 
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/qgfvwvry_image.png", 
    subtitle: "Farm & Property",
    description: "Practical rural fencing solutions for farms and large properties. From boundary fencing to stock control, built tough for Great Southern conditions.",
    gallery: [
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/qgfvwvry_image.png",
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/wrt20m0j_image.png",
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/2vjc2lid_image.png",
    ]
  },
  { 
    title: "COMMERCIAL", 
    image: "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/n93k06k2_image.png", 
    subtitle: "Business & Industrial",
    description: "Heavy-duty commercial fencing for businesses and industrial sites. Secure, compliant, and professionally installed to meet your project timelines.",
    gallery: [
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/n93k06k2_image.png",
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/uozweq9v_image.png",
      "https://customer-assets.emergentagent.com/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/rtpdzikr_image.png",
    ]
  },
];

// What We Do service data
const WHAT_WE_DO_SERVICES = [
  { 
    title: "Commercial Fencing", 
    shortDesc: "Durable, compliant, and built to perform.", 
    fullDesc: "Durable, compliant, and built to perform in demanding environments. We understand project timelines, site coordination, and the importance of getting it right without delays.",
    icon: "Building2",
    gradient: "from-blue-500 to-cyan-500"
  },
  { 
    title: "Residential Fencing", 
    shortDesc: "Clean, modern, privacy and security.", 
    fullDesc: "Clean, modern, and built to complement your home while providing privacy and security.",
    icon: "Home",
    gradient: "from-emerald-500 to-teal-500"
  },
  { 
    title: "Rural Fencing", 
    shortDesc: "Practical solutions for the Great Southern.", 
    fullDesc: "Practical, long-lasting solutions designed for the conditions of the Great Southern, from boundary fencing to stock control.",
    icon: "TreePine",
    gradient: "from-amber-500 to-orange-500"
  },
  { 
    title: "Premium Custom Solutions", 
    shortDesc: "Tailored fencing with high-end finish.", 
    fullDesc: "Tailored fencing and gate systems that combine function with a high-end finish, designed to stand out and last.",
    icon: "Sparkles",
    gradient: "from-purple-500 to-pink-500"
  },
];

// About Us value cards - EXACT from April 1
const ABOUT_VALUES = [
  {
    icon: Users,
    title: "Local Team",
    shortDesc: "100% Albany based team with local expertise.",
    fullDesc: "We're a 100% Albany-based team. We know the local conditions, we understand the region, and we're committed to supporting the community we live and work in. When you work with us, you're working with locals who care.",
    color: "cyan"
  },
  {
    icon: Shield,
    title: "Built to Last",
    shortDesc: "Understanding Albany's coastal conditions & soil variability.",
    fullDesc: "We don't cut corners. Every fence we build is constructed using quality materials, proven techniques, and attention to detail. Our work is built to withstand the test of time and the conditions of the Great Southern.",
    color: "emerald"
  },
  {
    icon: Award,
    title: "Quality First",
    shortDesc: "Uncompromising standards and consistent delivery.",
    fullDesc: "Quality isn't just a word to us - it's how we operate. From the first conversation to the final handover, we focus on delivering a result that exceeds expectations. That's why our clients keep coming back.",
    color: "yellow"
  },
  {
    icon: ThumbsUp,
    title: "We Back Our Work",
    shortDesc: "Taking pride in every project we complete.",
    fullDesc: "We stand behind everything we do. If something's not right, we'll make it right. Our reputation is built on trust, and we back our work with a commitment to customer satisfaction long after the job is done.",
    color: "purple"
  }
];

// What We Do Cards Component
const WhatWeDoCards = () => {
  const [activeService, setActiveService] = useState(null);
  
  const iconMap = {
    Building2: ({ className }) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
        <path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/>
      </svg>
    ),
    Home: ({ className }) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    TreePine: ({ className }) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"/>
        <path d="M12 22v-3"/>
      </svg>
    ),
    Sparkles: ({ className }) => (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
      </svg>
    ),
  };
  
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto text-left mt-10">
        {WHAT_WE_DO_SERVICES.map((service, i) => {
          const IconComponent = iconMap[service.icon];
          return (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ scale: 1.03, y: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveService(service)}
              className="relative bg-white rounded-2xl p-5 sm:p-6 border-2 border-emerald-200 hover:border-emerald-400 shadow-lg shadow-emerald-100/50 hover:shadow-xl hover:shadow-emerald-200/60 transition-all cursor-pointer group overflow-hidden"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              <div className="relative flex items-start gap-4">
                <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-[#0c1829] text-lg mb-1 group-hover:text-emerald-600 transition-colors">{service.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{service.shortDesc}</p>
                  
                  <div className="mt-3 flex items-center gap-1 text-emerald-500 text-xs font-semibold">
                    <span>Click for details</span>
                    <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
              
              <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${service.gradient} opacity-10 rounded-bl-[40px]`} />
            </motion.div>
          );
        })}
      </div>
      
      {/* Popup Modal */}
      <AnimatePresence>
        {activeService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setActiveService(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              className="w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-br from-[#0c1829] via-[#162844] to-[#0c1829] rounded-3xl overflow-hidden border border-cyan-500/30 shadow-[0_0_60px_rgba(6,182,212,0.2)]">
                <div className={`h-2 bg-gradient-to-r ${activeService.gradient}`} />
                
                <button 
                  onClick={() => setActiveService(null)} 
                  className="absolute top-4 right-4 text-white/50 hover:text-white z-20 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X size={20} />
                </button>
                
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${activeService.gradient} flex items-center justify-center shadow-lg`}>
                      {iconMap[activeService.icon] && iconMap[activeService.icon]({ className: "w-8 h-8 text-white" })}
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-black text-white">{activeService.title}</h3>
                      <p className={`text-transparent bg-clip-text bg-gradient-to-r ${activeService.gradient} text-sm font-medium`}>Albany Fencing Contractors</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/5 rounded-2xl p-5 sm:p-6 border border-white/10 mb-6">
                    <p className="text-white/90 text-lg sm:text-xl leading-relaxed">
                      {activeService.fullDesc}
                    </p>
                  </div>
                  
                  <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
                    <Button className={`w-full bg-gradient-to-r ${activeService.gradient} hover:opacity-90 text-white rounded-xl py-5 text-lg font-bold flex items-center justify-center gap-2 shadow-lg`}>
                      Get a Quote
                      <ChevronRight size={20} />
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Service Category Card - Taller cards matching reference design
const ServiceCategoryCard = ({ category, onClick }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden cursor-pointer group"
    >
      <div className="aspect-[3/4]">
        <img src={category.image} alt={category.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
        <h3 className="text-white font-bold text-base sm:text-lg tracking-wide">{category.title}</h3>
        <p className="text-cyan-300/80 text-xs mt-0.5">{category.subtitle}</p>
      </div>
    </motion.div>
  );
};

// Service Popup Modal - matches screenshot design
const ServicePopupModal = ({ category, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!category) return null;
  
  const gallery = category.gallery || [category.image];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 30 }}
        className="w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-[#0c1829] via-[#162844] to-[#0c1829] rounded-3xl overflow-hidden border border-cyan-500/30 shadow-[0_0_60px_rgba(6,182,212,0.15)]">
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white z-20 bg-black/30 hover:bg-black/50 rounded-full p-2 transition-colors"
          >
            <X size={20} />
          </button>
          
          {/* Main Image - no arrows, click thumbnails to change */}
          <div className="relative aspect-[16/10]">
            <img 
              src={gallery[currentImageIndex]} 
              alt={`${category.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c1829] via-transparent to-transparent opacity-80" />
          </div>
          
          {/* Content section */}
          <div className="p-6 pt-4">
            {/* Title with icon */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center">
                <Fence className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white tracking-wide">{category.title}</h3>
                <p className="text-cyan-400 text-sm">{category.subtitle}</p>
              </div>
            </div>
            
            {/* Description */}
            <p className="text-white/70 text-sm leading-relaxed mb-4">{category.description}</p>
            
            {/* Thumbnail strip - 3 images */}
            <div className="flex gap-2 mb-5">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-1 aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentImageIndex 
                      ? 'border-cyan-400 ring-2 ring-cyan-400/30' 
                      : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            
            {/* Get Quote Button */}
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-xl py-4 font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20">
                Get Quote for {category.title}
                <ChevronRight size={18} />
              </Button>
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Hero Section - Static grid on desktop, auto-scrolling carousel on mobile
const HeroSection = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [mobileCarouselPosition, setMobileCarouselPosition] = useState(0);
  
  // Auto-scroll the mobile carousel continuously
  useEffect(() => {
    const timer = setInterval(() => {
      setMobileCarouselPosition(prev => prev - 0.5);
    }, 20);
    return () => clearInterval(timer);
  }, []);
  
  // Triple the categories for seamless infinite scroll on mobile
  const extendedCategories = [...SERVICE_CATEGORIES, ...SERVICE_CATEGORIES, ...SERVICE_CATEGORIES];
  
  return (
    <section className="relative min-h-[35vh] lg:min-h-[32vh] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={PHOTOS.heroBackground} alt="Fencing" className="w-full h-full object-cover object-[center_25%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1829]/70 via-[#0c1829]/50 to-[#0c1829]/80" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-4">
        {/* Title with stars */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-2"
        >
          <h1 className="lg:hidden text-[26px] sm:text-[32px] font-black text-white mb-2 leading-tight whitespace-nowrap">
            Albany's <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Trusted</span> Fencing Contractors
          </h1>
          
          <h1 className="hidden lg:block text-5xl xl:text-6xl font-black text-white mb-2 leading-tight">
            Albany's <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Trusted</span> Fencing Contractors
          </h1>
          
          <div className="flex justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]" />
            ))}
          </div>
        </motion.div>

        {/* Desktop: Horizontal Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden lg:flex justify-center mb-10"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 blur-3xl rounded-full scale-110" />
            <img 
              src={LOGO.horizontal} 
              alt="Albany Fencing Contractors" 
              className="relative w-[620px] xl:w-[750px] h-auto rounded-2xl"
            />
          </div>
        </motion.div>

        {/* Mobile: Horizontal Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:hidden flex justify-center mb-14"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 blur-2xl rounded-full scale-110" />
            <img 
              src={LOGO.horizontal} 
              alt="Albany Fencing Contractors" 
              className="relative w-[340px] sm:w-[450px] h-auto rounded-xl"
            />
          </div>
        </motion.div>

        {/* Desktop: Static 4-Card Grid (NO carousel) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="hidden lg:block"
        >
          <div className="grid grid-cols-4 gap-4 max-w-5xl mx-auto mb-4">
            {SERVICE_CATEGORIES.map((category, i) => (
              <ServiceCategoryCard
                key={i}
                category={category}
                onClick={() => setActiveCategory(category)}
              />
            ))}
          </div>
          <p className="text-center text-white/50 text-sm">Tap a card to view samples</p>
        </motion.div>
        
        {/* Mobile: Auto-Scrolling Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:hidden overflow-hidden"
        >
          <div 
            className="flex gap-3 transition-none"
            style={{ 
              transform: `translateX(${mobileCarouselPosition % (170 * SERVICE_CATEGORIES.length)}px)`,
              width: `${extendedCategories.length * 170}px`
            }}
          >
            {extendedCategories.map((category, i) => (
              <div key={i} className="flex-shrink-0 w-[160px]">
                <ServiceCategoryCard
                  category={category}
                  onClick={() => setActiveCategory(category)}
                />
              </div>
            ))}
          </div>
          <p className="text-center text-white/50 text-xs mt-4">Tap a card to view samples</p>
        </motion.div>

        {/* GET FREE QUOTE Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex justify-center mt-6"
        >
          <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white px-8 py-6 rounded-2xl text-lg font-black shadow-lg shadow-emerald-500/30 flex items-center gap-2">
              <Phone size={20} />
              GET FREE QUOTE
              <ChevronRight size={20} />
            </Button>
          </a>
        </motion.div>

        {/* Mobile: Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="sm:hidden flex flex-col items-center mt-8 relative z-50"
        >
          <motion.p
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/50 text-xs tracking-[0.2em] uppercase mb-3"
          >
            Scroll to Explore
          </motion.p>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center"
          >
            <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="w-1.5 h-3 bg-cyan-400/80 rounded-full"
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Desktop: Scroll Prompt */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="hidden sm:flex flex-col items-center mt-10 mb-2"
        >
          <motion.p
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/50 text-xs tracking-[0.3em] uppercase mb-4"
          >
            Scroll
          </motion.p>
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center"
          >
            <div className="w-[2px] h-12 bg-gradient-to-b from-white/10 via-white/40 to-cyan-400/60 rounded-full" />
            <ChevronRight size={20} className="text-cyan-400/80 rotate-90 mt-1" />
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom wave */}
      <div className="absolute -bottom-1 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full block" preserveAspectRatio="none">
          <path d="M0,120 C360,40 1080,40 1440,120 L1440,120 L0,120 Z" fill="white" stroke="white"/>
        </svg>
      </div>
      
      {/* Service Popup Modal */}
      <AnimatePresence>
        {activeCategory && (
          <ServicePopupModal 
            category={activeCategory} 
            onClose={() => setActiveCategory(null)} 
          />
        )}
      </AnimatePresence>
    </section>
  );
};

// Transformation Section (Before/After) - Using new composite image
const TransformationSection = () => {
  return (
    <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0c1829]">
            See The <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">Transformation</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            Quality craftsmanship that speaks for itself — see what we can do for your property
          </p>
        </motion.div>
        
        {/* Before/After Composite Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            {/* Corner accents */}
            <div className="absolute -top-2 -left-2 w-10 h-10 border-t-4 border-l-4 border-emerald-400/60 rounded-tl-lg z-20" />
            <div className="absolute -bottom-2 -left-2 w-10 h-10 border-b-4 border-l-4 border-emerald-400/60 rounded-bl-lg z-20" />
            <div className="absolute -top-2 -right-2 w-10 h-10 border-t-4 border-r-4 border-emerald-400/60 rounded-tr-lg z-20" />
            <div className="absolute -bottom-2 -right-2 w-10 h-10 border-b-4 border-r-4 border-emerald-400/60 rounded-br-lg z-20" />
            
            {/* BEFORE label - left side */}
            <div className="absolute top-4 left-4 bg-[#0c1829] text-yellow-400 px-4 py-1.5 rounded-md font-bold text-sm z-10 tracking-wide shadow-lg">
              BEFORE
            </div>
            
            {/* AFTER label - right side */}
            <div className="absolute top-4 right-4 bg-emerald-500 text-white px-4 py-1.5 rounded-md font-bold text-sm z-10 tracking-wide shadow-lg">
              AFTER
            </div>
            
            {/* Composite before/after image */}
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/l5yo76qs_image.png" 
              alt="Before and After - Old beige fence transformed to modern dark Colorbond fence" 
              className="w-full object-cover"
              style={{ 
                filter: 'contrast(1.05) saturate(1.1) brightness(1.02)',
              }}
            />
            
            {/* Center divider line */}
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-1 bg-gradient-to-b from-white/80 via-white/60 to-white/80 z-10" />
          </div>
        </motion.div>
        
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 mt-10"
        >
          <div className="bg-white rounded-2xl px-10 py-5 text-center shadow-lg border border-slate-100">
            <p className="text-3xl sm:text-4xl font-black text-[#0c1829]">100%</p>
            <p className="text-slate-500 text-sm">Customer Satisfaction</p>
          </div>
          <div className="bg-white rounded-2xl px-10 py-5 text-center shadow-lg border border-slate-100">
            <p className="text-3xl sm:text-4xl font-black text-[#0c1829]">10yr+</p>
            <p className="text-slate-500 text-sm">Local Experience</p>
          </div>
        </motion.div>
        
        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mt-8"
        >
          <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white px-8 py-5 rounded-xl text-lg font-bold flex items-center gap-2 shadow-lg">
              <Phone size={20} />
              Get Your Free Quote
              <ChevronRight size={20} />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Auto-Scrolling Gallery Row Component
const AutoScrollGalleryRow = ({ images }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setScrollPosition(prev => prev - 0.5);
    }, 20);
    return () => clearInterval(timer);
  }, []);
  
  const extendedImages = [...images, ...images, ...images];
  
  return (
    <div className="overflow-hidden my-4">
      <div 
        className="flex gap-4 transition-none"
        style={{ 
          transform: `translateX(${scrollPosition % (320 * images.length)}px)`,
          width: `${extendedImages.length * 320}px`
        }}
      >
        {extendedImages.map((image, i) => (
          <div key={i} className="flex-shrink-0 w-[300px]">
            <div className="group relative aspect-[4/3] rounded-2xl overflow-hidden">
              <img src={image.url} alt={image.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                <p className="text-white font-semibold">{image.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// What We Do Section with static row + auto-scrolling carousel
const WhatWeDoSection = () => {
  return (
    <section id="gallery" className="py-16 sm:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-emerald-500 text-sm font-bold uppercase tracking-widest">Portfolio</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0c1829] mt-2">
            What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">Do</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Premium fencing solutions delivered across Albany and the Great Southern region.
          </p>
        </motion.div>
        
        {/* Service Cards with Popups */}
        <WhatWeDoCards />
        
        {/* Photo Gallery - Static Row + Carousel */}
        <div className="mt-12 space-y-6">
          {/* Row 1 - Static Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PORTFOLIO_IMAGES.row1.map((image, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden"
              >
                <img src={image.url} alt={image.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                  <p className="text-white font-semibold">{image.title}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Auto-Scrolling Carousel - At the bottom */}
          <AutoScrollGalleryRow images={PORTFOLIO_IMAGES.row2Carousel} />
        </div>
        
        {/* See All Services Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mt-10"
        >
          <Link to="/services">
            <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-2">
              See All Services
              <ArrowRight size={18} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// About Value Card Component
const AboutValueCard = ({ value, index, onClick }) => {
  const colorClasses = {
    cyan: "from-cyan-500 to-blue-500",
    emerald: "from-emerald-500 to-teal-500",
    yellow: "from-yellow-500 to-orange-500",
    purple: "from-purple-500 to-pink-500"
  };
  
  const IconComponent = value.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.03, y: -5 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-all cursor-pointer group"
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[value.color]} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
        <IconComponent className="w-6 h-6 text-white" />
      </div>
      <h4 className="text-white font-bold text-lg mb-2">{value.title}</h4>
      <p className="text-white/60 text-sm leading-relaxed mb-3">{value.shortDesc}</p>
      <div className="flex items-center gap-1 text-cyan-400 text-xs font-semibold">
        <span>Click for more</span>
        <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </motion.div>
  );
};

// About Section
const AboutSection = () => {
  const [activeValue, setActiveValue] = useState(null);
  
  const colorClasses = {
    cyan: "from-cyan-500 to-blue-500",
    emerald: "from-emerald-500 to-teal-500",
    yellow: "from-yellow-500 to-orange-500",
    purple: "from-purple-500 to-pink-500"
  };
  
  return (
    <section id="about" className="py-16 sm:py-24 bg-gradient-to-b from-[#0a1628] via-[#0c1829] to-[#0a1628] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-cyan-400 text-sm font-bold uppercase tracking-widest">About Us</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mt-2 leading-tight tracking-wide">
            Built on Reliability &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Quality</span>
          </h2>
        </motion.div>
        
        {/* Value Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {ABOUT_VALUES.map((value, i) => (
            <AboutValueCard
              key={i}
              value={value}
              index={i}
              onClick={() => setActiveValue(value)}
            />
          ))}
        </div>
        
        {/* Bottom Section - Tagline & Testimonial */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Tagline Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-2xl p-6 sm:p-8 border border-white/10"
          >
            <p className="text-white/90 text-lg sm:text-xl leading-relaxed mb-4">
              Our goal is simple: to be the <span className="text-cyan-400 font-semibold">preferred fencing company</span> in Albany.
            </p>
            <p className="text-cyan-300 text-base sm:text-lg italic font-medium">
              "At our core, we take pride in what we do, we back our work, and we enjoy doing it."
            </p>
          </motion.div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border border-white/10"
          >
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-white/80 italic text-base sm:text-lg leading-relaxed mb-4">
              "Excellent service and top-quality fence! The team was professional and efficient. Highly recommend!"
            </p>
            <p className="text-cyan-400 font-bold">— Satisfied Customer</p>
          </motion.div>
        </div>
        
        {/* Team & Vehicle Photos */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          {/* Truck Photo */}
          <div className="relative rounded-2xl overflow-hidden group">
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/7q4w50z2_IMG_9167.jpg" 
              alt="Albany Fencing Contractors branded truck" 
              className="w-full h-64 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c1829]/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4">
              <p className="text-white font-bold text-lg">Our Fleet</p>
              <p className="text-cyan-300 text-sm">Ready to serve Albany & surrounds</p>
            </div>
          </div>
          
          {/* Team Photo */}
          <div className="relative rounded-2xl overflow-hidden group">
            <img 
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_793d08ef-e496-4888-962e-68d1dae05874/artifacts/7eq4x2pi_IMG_9166.jpg" 
              alt="Albany Fencing Contractors team" 
              className="w-full h-64 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c1829]/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4">
              <p className="text-white font-bold text-lg">Our Team</p>
              <p className="text-cyan-300 text-sm">Local experts you can trust</p>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Value Popup Modal */}
      <AnimatePresence>
        {activeValue && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setActiveValue(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              className="w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-gradient-to-br from-[#0c1829] via-[#162844] to-[#0c1829] rounded-3xl overflow-hidden border border-cyan-500/30">
                <div className={`h-2 bg-gradient-to-r ${colorClasses[activeValue.color]}`} />
                
                <button 
                  onClick={() => setActiveValue(null)}
                  className="absolute top-4 right-4 text-white/50 hover:text-white z-20 bg-white/10 hover:bg-white/20 rounded-full p-2"
                >
                  <X size={20} />
                </button>
                
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colorClasses[activeValue.color]} flex items-center justify-center shadow-lg`}>
                      <activeValue.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-white">{activeValue.title}</h3>
                  </div>
                  
                  <div className="bg-white/5 rounded-2xl p-5 sm:p-6 border border-white/10">
                    <p className="text-white/90 text-lg sm:text-xl leading-relaxed">
                      {activeValue.fullDesc}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-16 sm:py-24 bg-gradient-to-b from-[#0a1628] to-[#0c1829] relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <img src={LOGO.main} alt="Albany Fencing" className="h-24 sm:h-32 mx-auto mb-6" />
          
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Contact us today for a free quote on your fencing project.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <a href={`tel:${PHONE_NUMBER.replace(/\s/g, '')}`}>
              <Button className="bg-white text-[#0c1829] hover:bg-white/90 px-8 py-5 rounded-xl font-bold text-lg flex items-center gap-2 w-full sm:w-auto justify-center">
                <Phone size={20} />
                {PHONE_NUMBER}
              </Button>
            </a>
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white px-8 py-5 rounded-xl font-bold text-lg flex items-center gap-2 w-full sm:w-auto justify-center">
                Request Quote
                <ExternalLink size={18} />
              </Button>
            </a>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center text-white/60">
            <div className="flex items-center gap-2 justify-center">
              <Mail size={18} />
              <span>albanyfc1@gmail.com</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <MapPin size={18} />
              <span>Albany, Western Australia</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Header
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#" },
    { name: "Services", href: "/services" },
    { name: "Products", href: "/products" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-[#0c1829]/95 backdrop-blur-md shadow-lg" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO.main} alt="Albany Fencing" className="h-10 sm:h-12" />
            <div className="hidden sm:block">
              <p className="text-white font-bold text-sm leading-tight">ALBANY FENCING</p>
              <p className="text-emerald-400 text-xs font-medium tracking-wider">CONTRACTORS</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.href.startsWith("/") ? (
                <Link key={link.name} to={link.href} className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                  {link.name}
                </Link>
              ) : (
                <a key={link.name} href={link.href} className="text-white/80 hover:text-white text-sm font-medium transition-colors">
                  {link.name}
                </a>
              )
            ))}
            <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white px-5 py-2 rounded-xl font-bold text-sm">
                <Sparkles size={16} className="mr-2" />
                GET QUOTE
              </Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden text-white p-2">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "100vh" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden fixed inset-0 top-16 bg-[#0c1829] z-40"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  {link.href.startsWith("/") ? (
                    <Link
                      to={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-white text-2xl font-bold"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="text-white text-2xl font-bold"
                    >
                      {link.name}
                    </a>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-10 py-5 rounded-2xl font-bold text-xl">
                    GET QUOTE
                  </Button>
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#060d18] py-8 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} Albany Fencing Contractors. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden scroll-smooth">
      <Header />
      <HeroSection />
      <TransformationSection />
      <WhatWeDoSection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
